import axios, { AxiosRequestConfig } from 'axios';
import { Env } from '../env';

export type ApiClientCallback = (err: ApiError | null, result) => void;

const DEFAULT_TIMEOUT = 3000;
const userPublicApiPrefix = '/api/';

/** APIのエラー */
export class ApiError extends Error {
  public name = 'ApiError';
  constructor(
    message: string, // messageフィールドはErrorのものを利用する
    readonly status: number = 0,
    readonly detail = {}
  ) {
    super(message);
  }
}

const createRequestConfig = (url: string, options?: AxiosRequestConfig): AxiosRequestConfig | undefined => {
  let urlObj: URL | null = null;

  try {
    if (Env.SETTING.userPublicApi.urlBase === '/') {
      urlObj = new URL(url, window.location.origin);
    } else {
      urlObj = new URL(url, Env.SETTING.userPublicApi.urlBase);
    }
  } catch (e) {
    return;
  }

  const optionsForEdit = options || {};
  if (urlObj && urlObj.pathname.startsWith(userPublicApiPrefix)) {
    optionsForEdit.headers = options?.headers || {};
    optionsForEdit.headers.Authorization = Env.SETTING.userPublicApi.authorizationCode;
    if (Env.USER_INFO.userId) {
      optionsForEdit.headers['X-USER-ID'] = Env.USER_INFO.userId;
    }
  }
  optionsForEdit.timeout = options?.timeout || DEFAULT_TIMEOUT;

  return optionsForEdit;
};

export class ApiClient {
  static readonly showAlertWhile400Or500Error = 'showAlertWhile400Or500Error';

  static async get(url: string, options?: AxiosRequestConfig) {
    return await (await axios.get(url, {withCredentials: true, ...createRequestConfig(url, options)})).data;
  }

  static async getWithStatusCode(url: string, options?: AxiosRequestConfig) {
    return axios
      .get(url, { withCredentials: true, ...createRequestConfig(url, options) })
      .catch((e) => ApiClient.catchError(url, e));
  }

  static async post(url: string, request: {}, options?: AxiosRequestConfig) {
    return await (await axios.post(url, request, {withCredentials: true, ...createRequestConfig(url, options)})).data;
  }
  static async postAsForm(url: string, request: {}, options?: AxiosRequestConfig) {
    const formData = new FormData();
    for (const key in request) {
      formData.append(key, request[key]);
    }
    return await (await axios.post(url, formData, {withCredentials: true, ...createRequestConfig(url, options)})).data;
  }

  static async postWithStatusCode(url: string, request: {}) {
    return axios.post(url, request, {withCredentials: true, ...createRequestConfig(url, undefined)});
  }

  static async postForAPIError(url: string, request: {}, options?: AxiosRequestConfig) {
    return axios
      .post(url, request, {
        withCredentials: true,
        ...createRequestConfig(url, options)
      })
      .catch((e) => ApiClient.catchError(url, e));
  }

  static async put(url: string, request: {}, callback: ApiClientCallback) {
    await axios.put(url, request, {withCredentials: true, ...createRequestConfig(url, undefined)}).catch((e) => {
      callback(e, null);
    });
  }

  static async delete(url: string) {
    return await (await axios.delete(url, {withCredentials: true, ...createRequestConfig(url, undefined)})).data;
  }

  static async deleteWithStatusCode(url: string) {
    return axios
      .delete(url, { withCredentials: true, ...createRequestConfig(url, undefined) })
      .catch((e) => ApiClient.catchError(url, e));
  }

  private static catchError(url: string, error: any) {
    ApiClient.errorStatusCheck(url, error);
    return error;
  }

  private static errorStatusCheck(url: string, error: any) {
    // Env.PAGE = nullの場合もあるので、初期化する必要
    Env.PAGE = Env.PAGE || {};
    if (
      (!error.response || error.response?.status >= 400) &&
      url.indexOf(ApiClient.showAlertWhile400Or500Error) > -1
    ) {
      // urlにshowAlertWhile400Or500Error引数が含まれる場合は、絶対Alertする
      alert('予期せぬエラーが発生しました。申し訳ありませんが再度お試しください。');
    }
    throw error;
  }
}
